import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Linkedin } from 'lucide-react';
import Newsletter from '../components/Newsletter';
import { studioImage, values, team, news } from '../mock';

const PageHero = ({ eyebrow, title, children }) => (
  <section className="pt-[140px] pb-16 px-6 lg:px-10">
    <div className="max-w-[1100px] mx-auto fade-up">
      <span className="dctv-eyebrow text-[#e8743b]">{eyebrow}</span>
      <h1 className="font-serif-dctv text-5xl md:text-6xl font-bold text-white mt-5">{title}</h1>
      {children}
    </div>
  </section>
);

const About = () => (
  <div>
    <PageHero eyebrow="About" title="Who we are">
      <p className="text-[#bcc3cf] text-lg leading-relaxed mt-6 max-w-2xl">
        Diaspora Community Television is a multicultural media platform giving the diaspora a voice - promoting social cohesion, dialogue and self-expression through community television.
      </p>
    </PageHero>

    <section className="px-6 lg:px-10 pb-16">
      <div className="max-w-[1100px] mx-auto">
        <div className="rounded-2xl overflow-hidden border border-white/8">
          <img src={studioImage} alt="DCTV studio" className="w-full h-[420px] object-cover object-[center_22%]" />
        </div>
        <div className="max-w-2xl mt-12 space-y-6 text-[#9aa3b2] text-[15px] leading-relaxed">
          <p>DCTV exists to make sure the communities of the diaspora can tell their own stories, in their own words. We produce and broadcast programming that reflects the cultures, languages and lived experiences of the people we serve.</p>
          <p>Our work spans news, forums, culture and documentary - all co-created with community members from the very first conversation. We believe media is strongest when it is made <em className="text-white not-italic font-medium">with</em> a community rather than simply <em className="text-white not-italic font-medium">about</em> one.</p>
          <p>Based in The Hague and connected across Europe, Mainland China and countries of origin, DCTV brings the diaspora together around shared dialogue and self-expression.</p>
        </div>
      </div>
    </section>

    <section className="px-6 lg:px-10 pb-12">
      <div className="max-w-[1100px] mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {values.map((v) => (
          <div key={v.title} className="dctv-card rounded-2xl p-7">
            <h3 className="font-serif-dctv text-2xl font-semibold text-white">{v.title}</h3>
            <p className="text-[#9aa3b2] text-sm leading-relaxed mt-3">{v.text}</p>
          </div>
        ))}
      </div>
      <div className="max-w-[1100px] mx-auto mt-10">
        <Link to="/programmes" className="dctv-btn-ghost inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold">
          Explore our programmes <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>

    <section className="px-6 lg:px-10 py-16 bg-[#0c1119] border-t border-white/8">
      <div className="max-w-[1100px] mx-auto">
        <span className="dctv-eyebrow text-[#e8743b]">Our people</span>
        <h2 className="font-serif-dctv text-3xl md:text-4xl font-bold text-white mt-4">Meet the team</h2>
        <p className="text-[#9aa3b2] text-[15px] leading-relaxed mt-4 max-w-2xl">
          The voices and editorial minds behind DCTV - bringing diaspora stories to screen with care and credibility.
        </p>
        <div className="grid sm:grid-cols-3 gap-6 mt-12">
          {team.map((m) => {
            const count = news.filter((n) => n.author && n.author.name === m.name).length;
            const inner = (
              <>
                <div className="w-full aspect-[4/5] overflow-hidden bg-[#0a0e16]">
                  <img src={m.avatar} alt={m.name} style={m.imgStyle} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif-dctv text-xl font-semibold text-white">{m.name}</h3>
                  <p className="text-[#e8743b] text-xs font-semibold tracking-wider uppercase mt-1.5">{m.role}</p>
                  <p className="text-[#9aa3b2] text-[13px] leading-relaxed mt-3">{m.bio}</p>
                  {count > 0 && (
                    <span className="inline-flex items-center gap-1.5 mt-4 text-[#e8743b] text-sm font-semibold">
                      Read their {count} {count === 1 ? 'story' : 'stories'}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  )}
                </div>
              </>
            );
            return (
              <div key={m.id} className="relative">
                {m.linkedin && (
                  <a
                    href={m.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`team-linkedin-${m.id}`}
                    aria-label={`${m.name} on LinkedIn`}
                    className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#0a0e16]/80 border border-white/15 text-white backdrop-blur transition-colors hover:bg-[#0A66C2] hover:border-[#0A66C2]"
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
                {count > 0 ? (
                  <Link
                    to={`/news?author=${encodeURIComponent(m.name)}`}
                    data-testid={`team-member-link-${m.id}`}
                    className="dctv-card group rounded-2xl overflow-hidden text-center block hover:border-[#e8743b]/40 transition-colors"
                  >
                    {inner}
                  </Link>
                ) : (
                  <div data-testid={`team-member-${m.id}`} className="dctv-card group rounded-2xl overflow-hidden text-center">
                    {inner}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>

    <Newsletter />
  </div>
);

export default About;
